// ProgramPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "react-bootstrap";
import ProgramTable from "../components/Program/ProgramTable";
import ModalDeleteProgram from "../components/Program/DeleteProgram";
import ModalShowProgram from "../components/Program/ShowProgram";
import ProgramFormModal from "../components/Program/ProgramFormData";

import { useTranslate } from "../hooks/LanguageProvider";
import { useClipboard } from "../utilities/functions";
import { Link, useParams } from "react-router-dom";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ProgramInterface {
  id_program: number;
  id_site?: number;
  program_date?: string;
  site?: string;
  name?: string;
  client_name?: string;
  departure?: string;
  destination?: string;
  type?: string;
  observation?: string;
  status: string;
  nom_conducteur?: string;
  prenom_conducteur?: string;
  immatriculation_vehicule?: string;
  PSN?: string;
}

const Programs: React.FC = () => {
  const { id_site, name } = useParams();
  const siteId = Number(id_site);
  const { translate } = useTranslate();
  const { copyToClipboard, copiedId } = useClipboard(
    translate("Matriculation Copied")
  );

  // -------- STATES IMPORTÉS DE TON MODAL -------- //
  const [list_Program, setListProgram] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({
    id_program: true,
    program_date: true,
    departure: true,
    destination: true,
    type: true,
    observation: true,
    status: true,
    nom_conducteur: true,
    immatriculation_vehicule: true,
    client_name: true,
  });

  const [sort, setSort] = useState<"ASC" | "DESC">("DESC");
  const [column, setColumn] = useState("id_site");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const id_user = localStorage.getItem("userid");


  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [filterDate, setFilterDate] = useState<string | null>(null);


  const [type, setType] = useState(id_user === "1" ? 0 : 2);
  const [searchValue, setSearchValue] = useState("");


  // Fonction passée à l'enfant
  const handleTypeSearch = (val: string) => {
    const numero = parseInt(val, 10);
    setType(numero);
    console.log("handleTypeSearch..........." + type);

  };

  // ------ MODALS ------ //
  const [showDeleteProgramModal, setShowDeleteProgramModal] = useState(false);
  const [showShowProgramModal, setShowShowProgramModal] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>( null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editProgramData, setEditProgramData] = useState<any | null>(null);

  // ------ HANDLERS ------ //
  const handleEditProgram = (program: any) => {
    setEditProgramData(program);
    setShowFormModal(true);
  };

  const handleNewProgram = () => {
    setEditProgramData(null);
    setShowFormModal(true);
  };

  const handleDeleteProgramModal = (id: number) => {
    setSelectedProgramId(id);
    setShowDeleteProgramModal(true);
  };

  const handleShowShowProgramModal = (id: number) => {
    setSelectedProgramId(id);
    setShowShowProgramModal(true);
  };

  // -------- FETCH PROGRAMS (copié à l’identique) -------- //
  const fetchPrograms = async (pageOverride?: number) => {
    try {
      setLoading(true);
      const pageToUse = pageOverride ?? currentPage;
      if (pageOverride) setCurrentPage(pageToUse);

      // Requête principale pour récupérer les programmes
      const res = await fetch(`${backendUrl}/api/program/search`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_user,
          limitValue: limit,
          currentPage: pageToUse,
          id_site,
          search: searchValue,// champ sur lequel filtrer
          type,
          column,
          sort,
          filters: {
            program_date: filterDate,
          },
        }),
      });

      if (!res.ok) throw new Error("Failed to fetch programs");
      const data = await res.json();

      // Requête pour récupérer le total filtré côté serveur
      const totalRes = await fetch(`${backendUrl}/api/program/totalpage`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_user,
          search: searchValue,
          type,
          id_site,
          filters: {
            program_date: filterDate,
          },
        }),
      });

      if (!totalRes.ok) throw new Error("Failed total count");
      const totalData = await totalRes.json();
      const totalCount = totalData[0]?.count || 0;

      setListProgram(Array.isArray(data) ? data : []);
      setTotal(totalCount);
      setPageCount(Math.ceil(totalCount / limit));
    } catch (e) {
      console.error(e);
      setListProgram([]);
      setTotal(0);
      setPageCount(0);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchPrograms(); // lance la récupération à chaque changement de dépendances
  }, [limit, currentPage, searchValue, type, column, sort, id_site, filterDate]);

  const menuItems = useMemo(() => {
    return [
      ...(id_user === "1" ? ["ID Program"] : []),
      "Program Date",
      "Client",
      "Departure",
      "Destination",
      "Type",
      "Driver",
      "Vehicle",
      "Observation",
      "Status",
    ];
  }, [id_user]);

  const columnsMap = {
    ...(id_user === "1" && { id_program: "ID" }),
    program_date: "Program Date",
    client_name: "Client",
    departure: "Departure",
    destination: "Destination",
    type: "Type",
    nom_conducteur: "Driver",
    immatriculation_vehicule: "Vehicle",
    observation: "Observation",
    status: "Status",
  };




  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value);
    setLimit(newLimit);
    setCurrentPage(1); // revenir à la première page
    fetchPrograms(1); // relancer fetch avec la nouvelle limite
  };

  // ------------------ RENDER PAGE ------------------ //
  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        {/* ---- LEFT ---- */}
        <h4>
          {" "}
          {name} :{" "}
          <span style={{ fontSize: "19px" }}>
            ({total}) {translate("Programs")}
          </span>{" "}
        </h4>

        {/* ---- RIGHT ---- */}
        <div className="d-flex">
          <Link
            to="/Sites"
            className="btn btn-outline-secondary mt-2 mr-1"
            data-toggle="tooltip"
            data-placement="top"
            title={translate("Back to Sites")}
          >
            <i
              className="ri-arrow-left-line mr-1"
              style={{ fontSize: "1.2em" }}
            ></i>
            {translate("Back to site")}
          </Link>

          <Button variant="primary" className="mt-2" onClick={handleNewProgram}>
            <i className="ri-add-line"></i> {translate("New Program")}
          </Button>
        </div>
      </div>

      <ProgramTable
        loading={loading}
        programs={list_Program}
        id_site={siteId}
        selectedColumns={selectedColumns}
        copiedId={copiedId}
        copyToClipboard={copyToClipboard}
        translate={(t: string) => t}
        sortColumn={column}
        sortOrder={sort}
        handleSortingColumn={(col) => {
          setColumn(col);
          setSort(sort === "ASC" ? "DESC" : "ASC");
        }}
        handleShowNewDeadlineModal={() => { }}
        handleShowShowProgramModal={handleShowShowProgramModal}
        handleDeleteProgramModal={handleDeleteProgramModal}
        handleEditProgramModal={handleEditProgram}
        getStatusIcon={(status) => <span>{status}</span>}
        initialColumns={selectedColumns}
        limit={limit}
        total={total}
        pageCount={pageCount}
        currentPage={currentPage}
        handleSelectChange={handleSelectChange}
        handlePageClick={(data) => fetchPrograms(data.selected + 1)}
        columnsMap={columnsMap}
        handleColumnChange={(col) =>
          setSelectedColumns((prev) => ({ ...prev, [col]: !prev[col] }))
        }
        menuItems={menuItems}
        handleTypeSearch={handleTypeSearch}
        search={searchValue}
        handleAdvancedSearch={(e) => setSearchValue(e.target.value)}
        filterDate={filterDate}
        onFilterDateChange={setFilterDate}
        fetchPrograms={fetchPrograms}
      />

      {/* Delete */}
      <ModalDeleteProgram
        show={showDeleteProgramModal}
        onHide={() => setShowDeleteProgramModal(false)}
        id_program={selectedProgramId}
        onSuccess={fetchPrograms}
      />

      {/* Details */}
      <ModalShowProgram
        show={showShowProgramModal}
        onHide={() => setShowShowProgramModal(false)}
        id_program={selectedProgramId}
      />

      {/* Create / Edit */}
      <ProgramFormModal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        id_site={siteId}
        editProgramData={editProgramData}
        onSuccess={() => {
          fetchPrograms();
          setShowFormModal(false);
        }}
      />
    </div>
  );
};

export default Programs;
